import React, { useState } from 'react';
import {
  Row, Col, Card, Form, Button, Spinner, Toast, ToastContainer, Alert
} from 'react-bootstrap';
import { FaFileDownload, FaEye } from 'react-icons/fa';
import axios from 'axios';
import Select from 'react-select';

const CCPAPolicyGeneratorTab = ({ userData, showNotification, region, refreshActivities,logToAccessLogs }) => {
  const [form, setForm] = useState({
    orgName: '',
    industry: '',
    location: '',
    activities: [],
    policyTypes: [],
    consumerRights: []
  });
  const [loading, setLoading] = useState(false);
  const [generatedPolicies, setGeneratedPolicies] = useState([]);

  const CCPA_ACTIVITY_OPTIONS = [
    { value: 'Customer Relationship Management', label: 'Customer Relationship Management' },
    { value: 'Marketing & Advertising', label: 'Marketing & Advertising' },
    { value: 'E-commerce Transactions', label: 'E-commerce Transactions' },
    { value: 'Employee Data Processing', label: 'Employee Data Processing' },
    { value: 'Website Analytics & Cookies', label: 'Website Analytics & Cookies' },
    { value: 'Third-Party Data Sharing', label: 'Third-Party Data Sharing' },
    { value: 'Targeted Advertising', label: 'Targeted Advertising' },
    { value: 'Data Sales to Third Parties', label: 'Data Sales to Third Parties' },
    { value: 'Automated Decision Making', label: 'Automated Decision Making' }
  ];

  const CCPA_CONSUMER_RIGHTS = [
    { value: 'Right to Know', label: 'Right to Know (Categories & Sources)' },
    { value: 'Right to Delete', label: 'Right to Delete' },
    { value: 'Right to Opt-Out', label: 'Right to Opt-Out of Sale/Sharing' },
    { value: 'Right to Correct', label: 'Right to Correct Inaccurate Information' },
    { value: 'Right to Limit', label: 'Right to Limit Use of Sensitive PI' },
    { value: 'Right to Non-Discrimination', label: 'Right to Non-Discrimination' }
  ];

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const handleReset = () => {
    setForm({
      orgName: '',
      industry: '',
      location: '',
      activities: [],
      policyTypes: [],
      consumerRights: []
    });
    setGeneratedPolicies([]);
  };

  const handlePreview = async () => {
    if (form.policyTypes.length === 0) {
      showNotification('Select at least one policy to preview.', 'warning');
      return;
    }

    const previewType = form.policyTypes[0];
    const typeSlug = previewType.toLowerCase().replace(/\s+/g, '-');
    
    try {
      const res = await axios.get(`https://5z4teiowya.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/policy-generator/sample/${typeSlug}`, {
        headers,
        responseType: 'blob'
      });
      
      const file = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      
      await logActivity(`Previewed CCPA ${previewType} sample policy`, "info");
    } catch (err) {
      console.error('Error previewing policy:', err);
      showNotification('Error previewing policy', 'danger');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      policyTypes: checked
        ? [...prevForm.policyTypes, value]
        : prevForm.policyTypes.filter((p) => p !== value),
    }));
  };

const downloadPolicy = async (policy) => {
  try {
    const res = await axios.get(
      `https://vvmi1d182b.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/policy-generator/download/${policy.id}`,
      {
        headers,
        responseType: 'blob' // ensures we receive binary data
      }
    );

    // ✅ Create Blob with correct MIME type for Word (.docx)
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // ✅ Change file extension from .txt → .docx
    link.setAttribute('download', `CCPA_${policy.name.replace(/\s+/g, '_')}.docx`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    await logActivity(`Downloaded CCPA ${policy.name}`, "primary");
    logToAccessLogs?.({
        action:      'Policy Downloaded',
        severity:    'info',
        description: `Downloaded CCPA policy: ${policy.name} for ${form.orgName}`,});
  } catch (error) {
    console.error('Error downloading policy:', error);
    showNotification('Error downloading policy', 'danger');
  }
};



  const handleGenerate = async () => {
    if (!form.orgName || !form.industry || !form.location || form.policyTypes.length === 0) {
      showNotification('Please fill all required fields.', 'danger');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form };
      
      const res = await axios.post(
        'https://kdbtbaggm0.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/policy-generator/create',
        payload,
        { headers }
      );
      
      setGeneratedPolicies(res.data.generated || []);
      showNotification('CCPA-compliant policies generated successfully!');
      await logActivity("Generated CCPA policies using policy generator", "success");
       logToAccessLogs?.({
        action:      'Policy Generated',
        severity:    'success',
        description: `Generated ${res.data.generated?.length ?? 0} CCPA policies for ${form.orgName} (${form.industry})`,});
    } catch (err) {
      console.error("Error generating policies:", err);
      showNotification('Error generating policies', 'danger');
    } finally {
      setLoading(false);
    }
  };

const logActivity = async (message, badge = "info") => {
 
  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "Policy Generator",              // Add module field
      description: message                  // message → details
      // userId and timestamp set by backend
    };
    
    
    
    const response = await axios.post(
      "https://wjioewyrif.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/create",  // ✅ Correct endpoint
      payload,     
      { headers }  // ✅ Send headers properly
    );
    
    
    if (refreshActivities) {
        await refreshActivities();
      }


  } catch (err) {
    console.error('❌ Error logging inventory activity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};

  return (
    <Card>
      <Card.Header>
        <h5>Generate CCPA-Compliant Policies</h5>
      </Card.Header>
      <Card.Body>
        <Alert variant="info">
          <strong>CCPA Policy Generator:</strong> This tool generates policies compliant with the California Consumer Privacy Act. 
          All policies include required CCPA elements such as consumer rights, data categories, and opt-out mechanisms.
        </Alert>
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="orgName"
                  value={form.orgName}
                  onChange={handleChange}
                  placeholder="Your organization name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Industry Sector *</Form.Label>
                <Form.Select name="industry" value={form.industry} onChange={handleChange}>
                  <option value="">Select Industry</option>
                  <option value="Retail & E-commerce">Retail & E-commerce</option>
                  <option value="Technology & Software">Technology & Software</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Healthcare & Life Sciences">Healthcare & Life Sciences</option>
                  <option value="Media & Entertainment">Media & Entertainment</option>
                  <option value="Education">Education</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Travel & Hospitality">Travel & Hospitality</option>
                  <option value="Automotive">Automotive</option>
                  <option value="Professional Services">Professional Services</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Primary Business Location *</Form.Label>
            <Form.Select name="location" value={form.location} onChange={handleChange}>
              <option value="">Select Location</option>
              <option value="California">California</option>
              <option value="California + Other US States">California + Other US States</option>
              <option value="California + International">California + International</option>
              <option value="Multi-State (including CA)">Multi-State (including CA)</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Business Activities</Form.Label>
            <Select
              isMulti
              options={CCPA_ACTIVITY_OPTIONS}
              value={CCPA_ACTIVITY_OPTIONS.filter(opt => form.activities.includes(opt.value))}
              onChange={(selected) => {
                setForm({ ...form, activities: selected.map(s => s.value) });
              }}
              placeholder="Select all applicable business activities..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Consumer Rights Supported</Form.Label>
            <Select
              isMulti
              options={CCPA_CONSUMER_RIGHTS}
              value={CCPA_CONSUMER_RIGHTS.filter(opt => form.consumerRights.includes(opt.value))}
              onChange={(selected) => {
                setForm({ ...form, consumerRights: selected.map(s => s.value) });
              }}
              placeholder="Select consumer rights your business supports..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Policy Types to Generate *</Form.Label>
            <Row>
              {[
                'CCPA Privacy Policy',
                'Consumer Rights Notice', 
                'Do Not Sell or Share Notice',
                'Cookie & Tracking Policy',
                'Data Processing Disclosure',
                'Sensitive Personal Information Notice',
                'Third-Party Data Sharing Policy',
                'Consumer Request Form Template'
              ].map((type) => (
                <Col md={6} key={type}>
                  <Form.Check
                    type="checkbox"
                    id={type}
                    label={type}
                    value={type}
                    checked={form.policyTypes.includes(type)}
                    onChange={handleCheckboxChange}
                  />
                </Col>
              ))}
            </Row>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleGenerate} disabled={loading}>
              {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              Generate Policies
            </Button>
            <Button variant="secondary" onClick={handleReset}>Reset</Button>
            <Button 
              variant="outline-info" 
              onClick={handlePreview}
              disabled={form.policyTypes.length === 0}
            >
              <FaEye /> Preview Sample
            </Button>
          </div>
        </Form>

        <hr />

        <div>
          <h6>Generated CCPA Policies</h6>
          {generatedPolicies.length === 0 ? (
            <Alert variant="secondary">No policies generated yet.</Alert>
          ) : (
            <Row>
              {generatedPolicies.map((policy, idx) => (
                <Col md={6} key={idx} className="mb-3">
                  <Card>
                    <Card.Body>
                      <Card.Title>{policy.name}</Card.Title>
                      <Card.Text className="text-muted">{policy.description}</Card.Text>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => downloadPolicy(policy)}
                      >
                        <FaFileDownload /> Download
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default CCPAPolicyGeneratorTab;
